import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PiggyBank, ArrowRight, RotateCcw } from "lucide-react";
import { scrollToId } from "@/pages/LandingPage";

const EASE = [0.22, 1, 0.36, 1];

const NEEDS = [
  { id: "will", label: "Will prepared", cost: 400 },
  { id: "contract", label: "Contract reviewed", cost: 350 },
  { id: "ticket", label: "Traffic ticket help", cost: 300 },
  { id: "letter", label: "Demand letter sent", cost: 275 },
  { id: "home", label: "Home purchase docs reviewed", cost: 500 },
  { id: "consult", label: "2 hrs of legal consultations", cost: 600 },
];

const MEMBERSHIP_ANNUAL = 479;

export default function SavingsCalculator() {
  const [selected, setSelected] = useState([]);

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const total = NEEDS.filter((n) => selected.includes(n.id)).reduce((sum, n) => sum + n.cost, 0);
  const savings = Math.max(total - MEMBERSHIP_ANNUAL, 0);

  return (
    <section id="savings" className="bg-[#F8FAFC] py-24 md:py-36 scroll-mt-20" data-testid="savings-calculator">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <motion.p
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, ease: EASE }}
          className="font-mono-accent text-xs uppercase tracking-[0.28em] text-blue-600 mb-6"
        >
          The napkin math
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ delay: 0.1, duration: 0.7, ease: EASE }}
          className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#0B192C] max-w-3xl leading-[1.1] mb-14"
        >
          Tap what life might throw at you this year.
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7 flex flex-wrap gap-3" data-testid="savings-needs">
            {NEEDS.map((n, i) => {
              const active = selected.includes(n.id);
              return (
                <motion.button
                  key={n.id}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06, duration: 0.5, ease: EASE }}
                  data-testid={`savings-chip-${n.id}`}
                  onClick={() => toggle(n.id)}
                  className={`flex items-center gap-3 rounded-2xl border px-5 py-4 text-left transition-all duration-300 ${
                    active
                      ? "bg-[#1D4ED8] border-[#1D4ED8] text-white shadow-lg shadow-blue-600/25 -translate-y-0.5"
                      : "bg-white border-slate-200 text-slate-700 hover:border-blue-400 hover:-translate-y-0.5"
                  }`}
                >
                  <span className="font-medium text-sm">{n.label}</span>
                  <span className={`font-mono-accent text-xs ${active ? "text-sky-300" : "text-slate-400"}`}>
                    ~${n.cost}
                  </span>
                </motion.button>
              );
            })}
            {selected.length > 0 && (
              <button
                data-testid="savings-reset-button"
                onClick={() => setSelected([])}
                className="flex items-center gap-2 px-5 py-4 text-sm text-slate-400 hover:text-blue-700 transition-colors duration-200"
              >
                <RotateCcw size={14} /> Reset
              </button>
            )}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7, ease: EASE }}
            className="lg:col-span-5 rounded-3xl bg-[#0B192C] grain-overlay relative overflow-hidden p-8 md:p-10"
            data-testid="savings-result-card"
          >
            <div className="absolute inset-0 hero-spotlight" />
            <div className="relative">
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-sky-400 mb-6">Your estimate</p>
              <div className="space-y-5 mb-8">
                <div className="flex items-baseline justify-between gap-4">
                  <span className="text-slate-400 text-sm">Traditional attorney</span>
                  <span className="font-display font-extrabold text-2xl text-white" data-testid="savings-total">
                    ${total.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-baseline justify-between gap-4">
                  <span className="text-slate-400 text-sm">Membership, a year</span>
                  <span className="font-display font-extrabold text-2xl text-sky-400">~${MEMBERSHIP_ANNUAL}</span>
                </div>
                <div className="h-px bg-white/10" />
                <div className="flex items-baseline justify-between gap-4">
                  <span className="text-slate-300 text-sm font-medium flex items-center gap-2">
                    <PiggyBank size={16} className="text-sky-400" /> You keep
                  </span>
                  <AnimatePresence mode="popLayout">
                    <motion.span
                      key={savings}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, ease: EASE }}
                      className="font-display font-extrabold text-4xl text-emerald-400"
                      data-testid="savings-amount"
                    >
                      ${savings.toLocaleString()}
                    </motion.span>
                  </AnimatePresence>
                </div>
              </div>
              <button
                data-testid="savings-cta-button"
                onClick={() => scrollToId("#get-started")}
                className="group w-full flex items-center justify-center gap-2 bg-sky-400 hover:bg-sky-300 text-[#0B192C] font-semibold py-3.5 rounded-full transition-colors duration-300"
              >
                Get real numbers from John
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <p className="text-[11px] text-slate-500 leading-relaxed mt-5">
                Attorney figures are illustrative national averages, not a quote. The membership
                figure reflects the entry-level plan at $39.95/mo — plans run $39.95 to $59.95/mo
                depending on coverage, and John will send exact figures before you decide anything.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
